import {
  HandleRegenerateOptionsType,
  type HandleSubmitMessageType,
  type HandleSubmitOptionsType,
} from '@/utils/aiUtils';
import { useChat } from '@ai-sdk/react';
import { useAuth } from '@clerk/clerk-react';
import {
  type ChatContext,
  type DtUIMessage,
  type TextMatchingRequest,
  type TextMatchingResponse,
} from '@ditto/shared';
import { useMutation } from '@tanstack/react-query';
import { DefaultChatTransport } from 'ai';
import { useAuthenticatedClient, useWorkspaceBaseUrl } from './useApi';

export function useChatWithAuthHeaders(getChatContext: () => ChatContext) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const workspaceBaseUrl = useWorkspaceBaseUrl();

  const {
    sendMessage: sendMessageWithoutAuth,
    regenerate: regenerateWithoutAuth,
    ...rest
  } = useChat<DtUIMessage>({
    resume: true,
    experimental_throttle: 100,
    transport: new DefaultChatTransport({
      prepareReconnectToStreamRequest: async () => {
        const token = await getToken();
        const chatContext = getChatContext();
        const chatId = chatContext.id;
        return {
          api: `${workspaceBaseUrl}/assistant/chat/${chatId}/stream`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      prepareSendMessagesRequest({ messages, body }) {
        return {
          api: `${workspaceBaseUrl}/assistant/chat`,
          body: {
            ...body,
            message: messages[messages.length - 1],
          },
        };
      },
    }),
  });

  const sendMessage = async (
    message: HandleSubmitMessageType,
    opts?: HandleSubmitOptionsType
  ) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User is not authenticated');
    }

    const token = await getToken();

    const chatContext = getChatContext();

    sendMessageWithoutAuth(message, {
      ...opts,
      body: {
        ...opts?.body,
        chatContext,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        ...opts?.headers,
      },
    });
  };

  const regenerate = async (opts?: HandleRegenerateOptionsType) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User is not authenticated');
    }

    const token = await getToken();

    const chatContext = getChatContext();

    regenerateWithoutAuth({
      ...opts,
      body: {
        ...opts?.body,
        chatContext,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        ...opts?.headers,
      },
    });
  };

  return { sendMessage, regenerate, ...rest };
}

export const useTextMatchingApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (
      data: TextMatchingRequest
    ): Promise<TextMatchingResponse> => {
      const response = await client.chat.findTextMatch({
        body: data,
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error(
        response.status === 400
          ? response.body.message
          : 'Failed to perform text matching'
      );
    },
  });
};
